import { useRef, useEffect, useCallback } from "react";
const SWIPE_THRESHOLD = 50;
function useSwipe({ onSwipeLeft, onSwipeRight, onDragMove, onDragEnd, }) {
    const startX = useRef(null);
    // Keep callbacks in refs so handlers never need to be recreated
    const onSwipeLeftRef = useRef(onSwipeLeft);
    const onSwipeRightRef = useRef(onSwipeRight);
    const onDragMoveRef = useRef(onDragMove);
    const onDragEndRef = useRef(onDragEnd);
    onSwipeLeftRef.current = onSwipeLeft;
    onSwipeRightRef.current = onSwipeRight;
    onDragMoveRef.current = onDragMove;
    onDragEndRef.current = onDragEnd;
    // Holds the global listener cleanup so they can be removed if the component
    // unmounts mid-drag.
    const cleanupMouseRef = useRef(null);
    const rafIdRef = useRef(null);
    const pendingOffsetRef = useRef(0);
    const cancelPendingRaf = useCallback(() => {
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
    }, []);
    useEffect(() => () => {
        var _a;
        (_a = cleanupMouseRef.current) === null || _a === void 0 ? void 0 : _a.call(cleanupMouseRef);
        cancelPendingRaf();
    }, [cancelPendingRaf]);
    const scheduleDragMove = useCallback((offsetPx) => {
        pendingOffsetRef.current = offsetPx;
        if (rafIdRef.current !== null) {
            return;
        }
        rafIdRef.current = requestAnimationFrame(() => {
            var _a;
            rafIdRef.current = null;
            (_a = onDragMoveRef.current) === null || _a === void 0 ? void 0 : _a.call(onDragMoveRef, pendingOffsetRef.current);
        });
    }, []);
    const settle = useCallback((endX) => {
        var _a;
        if (startX.current === null) {
            return;
        }
        cancelPendingRaf();
        const delta = startX.current - endX;
        startX.current = null;
        const didSwipe = Math.abs(delta) >= SWIPE_THRESHOLD;
        (_a = onDragEndRef.current) === null || _a === void 0 ? void 0 : _a.call(onDragEndRef, didSwipe);
        if (didSwipe) {
            if (delta > 0) {
                onSwipeLeftRef.current();
            }
            else {
                onSwipeRightRef.current();
            }
        }
    }, [cancelPendingRaf]);
    // Mouse: attach move/up to window so the drag tracks outside the element.
    const handleMouseDown = useCallback((e) => {
        startX.current = e.clientX;
        const onMove = (ev) => {
            if (startX.current === null) {
                return;
            }
            scheduleDragMove(ev.clientX - startX.current);
        };
        const onUp = (ev) => {
            cleanupMouseRef.current = null;
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            settle(ev.clientX);
        };
        cleanupMouseRef.current = () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    }, [settle, scheduleDragMove]);
    // Touch: the browser routes move/end to the originating element automatically.
    const handleTouchStart = useCallback((e) => {
        startX.current = e.touches[0].clientX;
    }, []);
    const handleTouchMove = useCallback((e) => {
        if (startX.current === null) {
            return;
        }
        scheduleDragMove(e.touches[0].clientX - startX.current);
    }, [scheduleDragMove]);
    const handleTouchEnd = useCallback((e) => {
        settle(e.changedTouches[0].clientX);
    }, [settle]);
    return {
        onMouseDown: handleMouseDown,
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
    };
}
export { SWIPE_THRESHOLD };
export default useSwipe;
